import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClientAutocomplete from "../../components/clientAutocomplete";
import LostSalesResultView from "../../components/lost-sales-result-view";
import LostSalesBulkTimeline from "../../components/lost-sales-bulk-timeline";
import LostSalesScopePicker from "../../components/lost-sales-scope-picker";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import { PAGE_FONT, useMetricsUi } from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { fieldSx } from "../manage-catalog/constants";
import useLostSalesEligibility from "./useLostSalesEligibility";
import {
  buildLostSalesPagePayload,
  getDefaultLostSalesDateRange,
  resolveDefaultLostSalesWarehouse,
} from "../../utils/lostSalesPageDefaults";
import { scopeFromOption } from "../../utils/lostSalesScopeOptions";
import {
  LOST_SALES_PARAMETER_HELP,
  describeLostSalesParameters,
} from "../../utils/lostSalesSettings";
import {
  getLostSalesErrorPresentation,
  isLostSalesErrorPayload,
} from "../../utils/lostSalesErrors";
import {
  BULK_MAX_TARGET_DATE_RANGE_DAYS,
  buildBulkLostSalesPayload,
  buildProductLabelById,
  buildSafeProductBatches,
  formatMetric,
  inclusiveDaySpan,
  mergeBulkLostSalesResults,
  resolveBulkProductLabel,
  maxScopesForLevel,
  scopesFromEligibleProducts,
  scopesFromEligibleSkus,
} from "../../utils/lostSalesBulk";
import {
  describeBulkRevenue,
  describeRevenueEstimate,
} from "../../utils/lostSalesRevenue";
import { lostSalesService } from "../../utils/services/lost-sales.service";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";

const FIELD_HEIGHT = 40;

const fieldControlSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_HEIGHT,
    fontFamily: PAGE_FONT,
    fontSize: 14,
  },
};

const FieldLabel = ({ children, colors }) => (
  <Typography
    sx={{
      fontSize: 13,
      mb: 0.5,
      color: colors.textSecondary,
      fontFamily: PAGE_FONT,
      lineHeight: 1.3,
    }}
  >
    {children}
  </Typography>
);

const LostSalesAnalysisPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const mobile = isMobile();
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClient, setSelectedClient] = useState(null);
  const clientId = selectedClient?._id || storedClientId;

  const [availabilityLoading, setAvailabilityLoading] = useState(() =>
    Boolean(storedClientId),
  );
  const [available, setAvailable] = useState(false);
  const [analysisLevel, setAnalysisLevel] = useState("sku");
  // The client's calculation settings, applied server-side to every run.
  const [calcSettings, setCalcSettings] = useState(null);

  // Normalized picker options (see utils/lostSalesScopeOptions) — SKUs for
  // SKU-level clients, products for product-level clients.
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  // "selected" = manual multi-select; "all" = warehouse-eligible All Products.
  const [productSelectionMode, setProductSelectionMode] = useState("selected");
  const [allProductsProgress, setAllProductsProgress] = useState(null);
  const [startDate, setStartDate] = useState(
    () => getDefaultLostSalesDateRange().start_date,
  );
  const [endDate, setEndDate] = useState(
    () => getDefaultLostSalesDateRange().end_date,
  );
  const [lookupError, setLookupError] = useState("");

  const [result, setResult] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [detailResult, setDetailResult] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isProductLevel = analysisLevel === "product";
  // "All population" mode: All Products for product-level clients, All SKUs for
  // SKU-level clients.
  const isAllPopulationMode = productSelectionMode === "all";
  const allPopulationLabel = isProductLevel ? "All Products" : "All SKUs";

  // The eligible population is only needed by All SKUs / All Products (it is
  // what that mode analyzes), so it loads only while that mode is on.
  const eligibility = useLostSalesEligibility({
    clientId,
    warehouseId: selectedWarehouse?._id,
    startDate,
    endDate,
    enabled: available && isAllPopulationMode,
  });
  const eligibleLoading = eligibility.loading;
  const eligibleProductCount = eligibility.rows ? eligibility.rows.length : null;
  const eligibleError =
    eligibility.error ||
    (eligibleProductCount === 0
      ? `No warehouse-eligible ${
          isProductLevel ? "products" : "SKUs"
        } with both inventory and sales history were found for the selected period.`
      : "");

  const scopes = useMemo(
    () => selectedOptions.map(scopeFromOption),
    [selectedOptions],
  );

  const productLabelById = useMemo(() => {
    if (isAllPopulationMode && bulkResult?.results?.length) {
      return buildProductLabelById(
        bulkResult.results.map((row) => ({
          _id: row.scope?.product,
          name: row.scope?.product_name,
        })),
      );
    }
    return buildProductLabelById(scopes.map((scope) => scope.product));
  }, [isAllPopulationMode, scopes, bulkResult]);

  const expandedSkuCount = useMemo(() => {
    if (!isProductLevel) return scopes.length;
    return scopes.reduce(
      (total, scope) => total + (scope.childSkuCount || 0),
      0,
    );
  }, [isProductLevel, scopes]);

  const invalidRange = !startDate || !endDate || startDate > endDate;
  const daySpan = inclusiveDaySpan(startDate, endDate);
  const exceedsDateLimit = daySpan > BULK_MAX_TARGET_DATE_RANGE_DAYS;
  const maxScopes = maxScopesForLevel(analysisLevel);
  const exceedsScopeLimit =
    !isAllPopulationMode && scopes.length > maxScopes;
  const missingSelectors = isAllPopulationMode
    ? !selectedWarehouse?._id ||
      exceedsDateLimit ||
      eligibleLoading ||
      Boolean(eligibleError) ||
      !eligibleProductCount
    : !selectedWarehouse?._id ||
      scopes.length === 0 ||
      exceedsScopeLimit ||
      exceedsDateLimit;

  const selectedInventory = useMemo(() => {
    const first = scopes[0];
    return {
      product: first?.product || null,
      warehouse: selectedWarehouse,
      sku: first?.sku || null,
    };
  }, [scopes, selectedWarehouse]);

  const cardSx = ui.card;

  const clearResults = () => {
    setResult(null);
    setBulkResult(null);
    setDetailResult(null);
    setError(null);
    setAllProductsProgress(null);
  };

  const runAnalysis = useCallback(
    async (payload) => {
      setSubmitting(true);
      clearResults();
      try {
        const analysis = await lostSalesService.analyze(payload);
        if (isLostSalesErrorPayload(analysis)) {
          setError(
            getLostSalesErrorPresentation(analysis, {
              warehouse: selectedWarehouse,
              startDate: payload.start_date,
              endDate: payload.end_date,
            }),
          );
          return;
        }
        setResult(analysis);
      } catch (requestError) {
        setError(
          getLostSalesErrorPresentation(requestError, {
            warehouse: selectedWarehouse,
            startDate: payload.start_date,
            endDate: payload.end_date,
          }),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [selectedWarehouse],
  );

  const runBulkAnalysis = useCallback(
    async (payload) => {
      setSubmitting(true);
      clearResults();
      try {
        const analysis = await lostSalesService.analyzeBulk(payload);
        if (isLostSalesErrorPayload(analysis)) {
          setError(
            getLostSalesErrorPresentation(analysis, {
              warehouse: selectedWarehouse,
              startDate: payload.start_date,
              endDate: payload.end_date,
            }),
          );
          return;
        }
        if (analysis?.bulk) {
          setBulkResult(analysis);
        } else {
          setResult(analysis);
        }
      } catch (requestError) {
        setError(
          getLostSalesErrorPresentation(requestError, {
            warehouse: selectedWarehouse,
            startDate: payload.start_date,
            endDate: payload.end_date,
          }),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [selectedWarehouse],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId) {
        setAvailabilityLoading(false);
        setAvailable(false);
        clearResults();
        return;
      }
      try {
        setAvailabilityLoading(true);
        setAvailable(false);
        clearResults();
        setLookupError("");
        setSelectedOptions([]);
        setSelectedWarehouse(null);
        setProductSelectionMode("selected");
        const data = await lostSalesService.availability(clientId);
        if (cancelled) return;
        const isAvailable = data?.available === true;
        if (!isAvailable) {
          setAvailable(false);
          return;
        }
        const level =
          data?.analysis_level === "product" ? "product" : "sku";
        setAnalysisLevel(level);
        setCalcSettings(data?.settings || null);

        try {
          const warehouse = await resolveDefaultLostSalesWarehouse(clientId);
          if (cancelled) return;
          setSelectedWarehouse(warehouse);
        } catch (_error) {
          if (!cancelled) {
            setLookupError(
              "The default warehouse could not be loaded. Select one to run analysis.",
            );
          }
        }
        if (cancelled) return;
        setAvailable(true);
        // Only the warehouse is pre-filled; the user picks SKUs and nothing
        // runs until they click Run Analysis.
      } catch (_error) {
        if (!cancelled) setAvailable(false);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleScopeChange = (next) => {
    setError(null);
    setSelectedOptions(next);
    setBulkResult(null);
    setDetailResult(null);
  };

  const runAllProductsAnalysis = useCallback(async () => {
    if (!clientId || !selectedWarehouse?._id) return;
    setSubmitting(true);
    clearResults();
    try {
      // Reuse the population already loaded for these params (the query can
      // take seconds on large warehouses); fetch only if it isn't ready.
      const population = eligibility.rows
        ? { analysis_level: analysisLevel, eligible_products: eligibility.rows }
        : await lostSalesService.eligibleProducts(
            clientId,
            selectedWarehouse._id,
            startDate,
            endDate,
          );
      if (isLostSalesErrorPayload(population)) {
        setError(
          getLostSalesErrorPresentation(population, {
            warehouse: selectedWarehouse,
            startDate,
            endDate,
          }),
        );
        return;
      }
      // Grain comes from the server (this response, or the availability
      // response the cached population was fetched under).
      const eligibleRows = population?.eligible_products || [];
      const scopesForAll =
        population?.analysis_level === "sku"
          ? scopesFromEligibleSkus(eligibleRows)
          : scopesFromEligibleProducts(eligibleRows);
      if (!scopesForAll.length) {
        setError({
          title: `No eligible ${isProductLevel ? "products" : "SKUs"}`,
          body: `No warehouse-eligible ${
            isProductLevel ? "products" : "SKUs"
          } with both inventory and sales history were found for the selected period.`,
        });
        return;
      }

      const batches = buildSafeProductBatches(scopesForAll);
      const payloads = batches.map((batchScopes) =>
        buildBulkLostSalesPayload({
          scopes: batchScopes,
          warehouseId: selectedWarehouse._id,
          clientId,
          startDate,
          endDate,
          includeBreakdown: "monthly",
        }),
      );

      let productsCompleted = 0;
      const batchResponses = [];
      for (let index = 0; index < payloads.length; index += 1) {
        const batchScopes = batches[index];
        setAllProductsProgress({
          batchIndex: index + 1,
          batchCount: payloads.length,
          productsCompleted,
          productsTotal: scopesForAll.length,
          productsInFlight: batchScopes.length,
        });

        const analysis = await lostSalesService.analyzeBulk(payloads[index]);
        if (isLostSalesErrorPayload(analysis)) {
          // Keep any completed batches; stop remaining work on hard batch failure.
          if (batchResponses.length) {
            setBulkResult(mergeBulkLostSalesResults(batchResponses));
          }
          setError(
            getLostSalesErrorPresentation(analysis, {
              warehouse: selectedWarehouse,
              startDate,
              endDate,
            }),
          );
          return;
        }
        batchResponses.push(analysis);
        productsCompleted += batchScopes.length;
        setAllProductsProgress({
          batchIndex: index + 1,
          batchCount: payloads.length,
          productsCompleted,
          productsTotal: scopesForAll.length,
          productsInFlight: 0,
        });
      }

      const merged = mergeBulkLostSalesResults(batchResponses);
      setBulkResult(merged);
    } catch (requestError) {
      setError(
        getLostSalesErrorPresentation(requestError, {
          warehouse: selectedWarehouse,
          startDate,
          endDate,
        }),
      );
    } finally {
      setSubmitting(false);
      setAllProductsProgress(null);
    }
  }, [
    clientId,
    selectedWarehouse,
    startDate,
    endDate,
    isProductLevel,
    analysisLevel,
    eligibility.rows,
  ]);

  const handleRunAnalysis = () => {
    if (submitting || invalidRange || missingSelectors || !clientId) return;

    if (isAllPopulationMode) {
      runAllProductsAnalysis();
      return;
    }

    if (scopes.length === 1) {
      runAnalysis(
        buildLostSalesPagePayload({
          productId: scopes[0].productId,
          warehouseId: selectedWarehouse._id,
          clientId,
          sku: scopes[0].sku,
          startDate,
          endDate,
        }),
      );
      return;
    }

    runBulkAnalysis(
      buildBulkLostSalesPayload({
        scopes,
        warehouseId: selectedWarehouse._id,
        clientId,
        startDate,
        endDate,
        includeBreakdown: "monthly",
      }),
    );
  };

  const openDetail = async (row) => {
    if (!row || row.status !== "complete" || !bulkResult?.shared) return;
    setDetailLoading(true);
    setDetailResult(null);
    try {
      const shared = bulkResult.shared;
      const analysis = await lostSalesService.analyze({
        product: row.scope.product,
        warehouse_id: shared.warehouse_id,
        client: shared.client,
        sku: row.scope.sku || undefined,
        start_date: shared.start_date,
        end_date: shared.end_date,
        search_window_days: shared.search_window_days,
        demand_window_days: shared.demand_window_days,
        dow_adjustment_enabled: shared.dow_adjustment_enabled,
      });
      if (isLostSalesErrorPayload(analysis)) {
        setError(getLostSalesErrorPresentation(analysis));
        return;
      }
      setDetailResult(analysis);
    } catch (requestError) {
      setError(getLostSalesErrorPresentation(requestError));
    } finally {
      setDetailLoading(false);
    }
  };

  const loading = availabilityLoading || submitting;
  const populationNoun = isProductLevel ? "products" : "SKUs";
  const initialStateMessage = isAllPopulationMode
    ? `Select Run Analysis to estimate units lost to stockouts across every ${
        isProductLevel ? "product" : "SKU"
      } with inventory and sales in this period.`
    : scopes.length
      ? "Select Run Analysis to estimate units lost to stockouts. Nothing runs until you do."
      : !selectedWarehouse?._id
        ? `Select a warehouse, then pick the ${populationNoun} to analyze.`
        : `Pick the ${populationNoun} to analyze, or switch to ${allPopulationLabel} to analyze every ${
            isProductLevel ? "product" : "SKU"
          } with inventory and sales in this period.`;
  const paramGridSx = {
    opacity: submitting ? 0.55 : 1,
    pointerEvents: submitting ? "none" : "auto",
    display: "grid",
    gap: mobile ? 1.5 : 1.75,
    gridTemplateColumns: mobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
    alignItems: "start",
  };

  return (
    <Box
      sx={{
        backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
        position: "relative",
        // Neutralise the global `.App { text-align: center }` so labels,
        // headings and the back link align left like the rest of the app.
        textAlign: "left",
      }}
    >
      {loading && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }}
        />
      )}
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="LS"
          title="Lost Sales Analysis"
          clientName={selectedClient?.business_name || selectedClient?.name}
          right={
            !storedClientId ? (
              <ClientAutocomplete
                width={220}
                selectedClient={selectedClient}
                onClientChange={setSelectedClient}
              />
            ) : null
          }
        />

        {clientId && availabilityLoading && !available && (
          <SalesPerformancePageLoading
            title="Loading lost sales analysis..."
            kpiCount={4}
          />
        )}

        {!clientId ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to estimate sales missed when inventory was too low
            to meet demand.
          </Alert>
        ) : !availabilityLoading && !available ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Lost Sales Analysis isn’t available yet. It becomes available once
            enough historical inventory and sales history has been collected.
          </Alert>
        ) : available ? (
          <>
            <Paper
              elevation={0}
              sx={{ ...cardSx, p: mobile ? 1.5 : 2, mb: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: PAGE_FONT,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Analysis Parameters
                </Typography>
                {(
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={productSelectionMode}
                    onChange={(_event, value) => {
                      if (!value) return;
                      setError(null);
                      setProductSelectionMode(value);
                      setBulkResult(null);
                      setDetailResult(null);
                    }}
                    disabled={submitting}
                    sx={{
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontFamily: PAGE_FONT,
                        fontSize: 12,
                        px: 1.25,
                        py: 0.25,
                      },
                    }}
                  >
                    <ToggleButton value="selected">Selected</ToggleButton>
                    <ToggleButton value="all">{allPopulationLabel}</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: PAGE_FONT,
                    fontWeight: 600,
                    fontSize: 12,
                    color: colors.textPrimary,
                    lineHeight: 1.4,
                  }}
                >
                  {isProductLevel
                    ? "Product-level analysis"
                    : "SKU-level analysis"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: PAGE_FONT,
                    fontSize: 12,
                    color: colors.textSecondary,
                    lineHeight: 1.45,
                  }}
                >
                  {isProductLevel
                    ? "Each selected product is analyzed as one pooled unit."
                    : "Each selected SKU is analyzed independently."}
                  {!isAllPopulationMode && scopes.length > 0 && (
                    <>
                      {" "}
                      <Box
                        component="span"
                        sx={{ color: colors.textPrimary, fontWeight: 500 }}
                      >
                        {scopes.length}{" "}
                        {isProductLevel
                          ? scopes.length === 1
                            ? "product"
                            : "products"
                          : scopes.length === 1
                            ? "SKU"
                            : "SKUs"}{" "}
                        selected
                        {isProductLevel && expandedSkuCount > 0
                          ? ` · ${expandedSkuCount} child SKUs`
                          : ""}
                      </Box>
                    </>
                  )}
                  {isAllPopulationMode && (
                    <>
                      {" "}
                      <Box
                        component="span"
                        sx={{ color: colors.textPrimary, fontWeight: 500 }}
                      >
                        {eligibleLoading
                          ? `Loading eligible ${
                              isProductLevel ? "products" : "SKUs"
                            }…`
                          : eligibleProductCount != null
                            ? `${eligibleProductCount.toLocaleString()} warehouse-eligible ${
                                isProductLevel ? "products" : "SKUs"
                              }`
                            : `All warehouse-eligible ${
                                isProductLevel ? "products" : "SKUs"
                              }`}
                      </Box>
                    </>
                  )}
                </Typography>
                {calcSettings?.parameters && (
                  <Box
                    data-testid="lost-sales-calc-settings"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      columnGap: 0.75,
                      mt: 0.5,
                      fontFamily: PAGE_FONT,
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                      {calcSettings.source === "client"
                        ? "Client calculation settings:"
                        : "Standard calculation settings:"}
                    </Box>
                    {describeLostSalesParameters(calcSettings.parameters).map(
                      ({ key, label }, index) => (
                        <React.Fragment key={key}>
                          {index > 0 && <Box component="span" aria-hidden>·</Box>}
                          <Tooltip title={LOST_SALES_PARAMETER_HELP[key]}>
                            <Box component="span" sx={{ cursor: "help" }}>
                              {label}
                            </Box>
                          </Tooltip>
                        </React.Fragment>
                      ),
                    )}
                    <Tooltip title="Set per client in Client Config → Dashboard settings → Lost Sales Analysis.">
                      <InfoOutlinedIcon
                        aria-label="Where to change calculation settings"
                        sx={{ fontSize: 14, cursor: "help" }}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Box>
              {submitting && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.25,
                    px: 1.5,
                    py: 1,
                    borderRadius: "10px",
                    border: `1px solid ${colors.border}`,
                    bgcolor: "action.hover",
                  }}
                >
                  <CircularProgress size={18} thickness={4} />
                  <Typography
                    sx={{
                      fontFamily: PAGE_FONT,
                      fontSize: 13,
                      color: colors.textSecondary,
                    }}
                  >
                    {allProductsProgress
                      ? `Analyzing ${
                          isProductLevel ? "all products" : "all SKUs"
                        } — ${allProductsProgress.batchIndex} / ${allProductsProgress.batchCount} batches (${allProductsProgress.productsCompleted.toLocaleString()} / ${allProductsProgress.productsTotal.toLocaleString()} ${
                          isProductLevel ? "products" : "SKUs"
                        })`
                      : "Analyzing data... This may take a few moments."}
                  </Typography>
                </Box>
              )}
              {lookupError && (
                <Alert severity="warning" sx={{ mb: 1.25 }}>
                  {lookupError}
                </Alert>
              )}
              {eligibleError && isAllPopulationMode && (
                <Alert severity="warning" sx={{ mb: 1.25 }}>
                  {eligibleError}
                </Alert>
              )}
              {(exceedsScopeLimit || exceedsDateLimit) && (
                <Alert severity="warning" sx={{ mb: 1.25 }}>
                  {exceedsScopeLimit
                    ? `Too many scopes (${scopes.length}). Maximum is ${maxScopes}.`
                    : `Date range of ${daySpan} days exceeds the maximum of ${BULK_MAX_TARGET_DATE_RANGE_DAYS} days.`}
                </Alert>
              )}
              <Box sx={paramGridSx}>
                <Box sx={{ minWidth: 0, gridColumn: mobile ? "auto" : "1 / 3" }}>
                  <FieldLabel colors={colors}>
                    {isProductLevel ? "Products" : "SKUs"}
                  </FieldLabel>
                  {isAllPopulationMode ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "8px",
                        bgcolor: "var(--bg-paper)",
                        height: FIELD_HEIGHT,
                      }}
                    >
                      <Chip
                        size="small"
                        color="primary"
                        label={
                          eligibleLoading
                            ? `${allPopulationLabel}…`
                            : eligibleProductCount != null
                              ? `${allPopulationLabel} (${eligibleProductCount.toLocaleString()})`
                              : allPopulationLabel
                        }
                      />
                      <Typography
                        sx={{
                          fontFamily: PAGE_FONT,
                          fontSize: 12,
                          color: colors.textSecondary,
                        }}
                      >
                        Inventory + sales in period
                      </Typography>
                    </Box>
                  ) : (
                    <LostSalesScopePicker
                      key={`scope-${clientId}-${analysisLevel}`}
                      grain={analysisLevel}
                      clientId={clientId}
                      value={selectedOptions}
                      onChange={handleScopeChange}
                      maxSelections={maxScopes}
                      disabled={submitting}
                    />
                  )}
                </Box>
                <Box>
                  <FieldLabel colors={colors}>Warehouse</FieldLabel>
                  <AnalyticsWarehousePicker
                    clientId={clientId}
                    mode="single"
                    allowAll={false}
                    value={selectedWarehouse}
                    onChange={(warehouse) => {
                      setError(null);
                      setSelectedWarehouse(warehouse || null);
                    }}
                    placeholder="Select a warehouse"
                    disabled={submitting || !clientId}
                    width="100%"
                    height={FIELD_HEIGHT}
                  />
                </Box>
                <Box>
                  <FieldLabel colors={colors}>Start Date</FieldLabel>
                  <TextField
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(event) => {
                      setError(null);
                      setStartDate(event.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={submitting}
                    sx={fieldControlSx}
                  />
                </Box>
                <Box>
                  <FieldLabel colors={colors}>End Date</FieldLabel>
                  <TextField
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(event) => {
                      setError(null);
                      setEndDate(event.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={submitting}
                    error={invalidRange || exceedsDateLimit}
                    helperText={
                      invalidRange
                        ? "End Date must be on or after Start Date."
                        : exceedsDateLimit
                          ? `Maximum range is ${BULK_MAX_TARGET_DATE_RANGE_DAYS} days.`
                          : undefined
                    }
                    sx={fieldControlSx}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                  }}
                >
                  {!mobile && (
                    <FieldLabel colors={colors}>
                      <Box component="span" aria-hidden>
                        &nbsp;
                      </Box>
                    </FieldLabel>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={submitting || invalidRange || missingSelectors}
                    onClick={handleRunAnalysis}
                    startIcon={
                      submitting ? (
                        <CircularProgress
                          size={16}
                          color="inherit"
                          thickness={5}
                        />
                      ) : null
                    }
                    sx={{
                      textTransform: "none",
                      fontFamily: PAGE_FONT,
                      fontWeight: 600,
                      height: FIELD_HEIGHT,
                      boxShadow: "none",
                    }}
                  >
                    {submitting ? "Analyzing..." : "Run Analysis"}
                  </Button>
                </Box>
              </Box>
            </Paper>

            {!submitting &&
              !error &&
              !result &&
              !bulkResult &&
              !detailLoading &&
              !detailResult && (
                <Alert
                  severity="info"
                  data-testid="lost-sales-initial-state"
                  sx={{ mb: 2, borderRadius: "10px" }}
                >
                  {initialStateMessage}
                </Alert>
              )}

            {submitting && !result && !bulkResult && (
              <SalesPerformancePageLoading
                title="Loading lost sales analysis..."
                kpiCount={4}
              />
            )}

            {error && !submitting && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                <Typography
                  sx={{ fontFamily: PAGE_FONT, fontWeight: 600, mb: 0.5 }}
                >
                  {error.title}
                </Typography>
                <Typography sx={{ fontFamily: PAGE_FONT, fontSize: 14 }}>
                  {error.body}
                </Typography>
              </Alert>
            )}

            {(detailLoading || detailResult) && bulkResult?.bulk && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  mb: 1.5,
                  py: 0.75,
                  bgcolor: darkMode ? colors.bgPrimary : "#F5F6F8",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDetailResult(null)}
                  disabled={detailLoading}
                  sx={{
                    textTransform: "none",
                    fontFamily: PAGE_FONT,
                    fontWeight: 600,
                    px: 1.25,
                    bgcolor: "var(--bg-paper)",
                  }}
                >
                  ← Back to bulk results
                </Button>
              </Box>
            )}

            {detailLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!detailLoading && detailResult?.status === "complete" && (
              <Paper elevation={0} sx={{ ...cardSx, p: mobile ? 1.5 : 2.5 }}>
                <LostSalesResultView
                  result={detailResult}
                  selectedInventory={{
                    // The single-scope response carries only the product id;
                    // resolve the name the bulk table already shows.
                    product: resolveBulkProductLabel(
                      detailResult.scope,
                      productLabelById,
                    ),
                    sku: detailResult.scope?.sku,
                    warehouse: selectedWarehouse,
                  }}
                  showHeading
                />
              </Paper>
            )}

            {!detailLoading &&
              !detailResult &&
              bulkResult?.bulk &&
              bulkResult.status === "complete" && (
                <Paper
                  elevation={0}
                  sx={{ ...cardSx, p: mobile ? 1.5 : 2.5 }}
                >
                  <DashboardBulkResults
                    bulkResult={bulkResult}
                    productLabelById={productLabelById}
                    onOpenDetail={openDetail}
                  />
                </Paper>
              )}

            {!detailLoading &&
              !bulkResult &&
              result?.status === "complete" && (
                <Paper elevation={0} sx={{ ...cardSx, p: mobile ? 1.5 : 2.5 }}>
                  <LostSalesResultView
                    result={result}
                    selectedInventory={selectedInventory}
                    showHeading
                  />
                </Paper>
              )}
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const DashboardBulkResults = ({
  bulkResult,
  productLabelById,
  onOpenDetail,
}) => {
  const summary = bulkResult.summary || {};
  const shared = bulkResult.shared || {};
  const rows = bulkResult.results || [];
  const failedRows = rows.filter((row) => row.status === "error");
  const revenueSummary = describeBulkRevenue(summary.estimated_lost_revenue);
  const failureCounts = failedRows.reduce((acc, row) => {
    const code = row.code || "ERROR";
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 600, fontSize: 16 }}>
        Bulk Results
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Chip label={`Requested: ${summary.requested ?? 0}`} />
        <Chip
          color="success"
          label={`Succeeded: ${summary.succeeded ?? 0}`}
        />
        <Chip
          color={summary.failed ? "error" : "default"}
          label={`Failed: ${summary.failed ?? 0}`}
        />
        <Chip
          label={`Lost sales: ${formatMetric(summary.total_estimated_lost_units)}`}
        />
        {revenueSummary?.totals.map((total) => (
          <Chip
            key={total}
            data-testid="bulk-revenue-chip"
            title="Estimated at current selling prices"
            label={`Est. lost revenue: ${total}`}
          />
        ))}
        <Chip
          label={`${shared.start_date || "—"} → ${shared.end_date || "—"}`}
        />
      </Box>
      {revenueSummary?.multiCurrency && (
        <Alert severity="info" sx={{ borderRadius: "10px" }}>
          Revenue is shown separately per currency; amounts in different
          currencies are not combined.
        </Alert>
      )}
      {revenueSummary?.note && (
        <Alert severity="warning" sx={{ borderRadius: "10px" }}>
          {revenueSummary.note} Revenue is an estimate at current selling
          prices.
        </Alert>
      )}
      {summary.failed > 0 && (
        <Alert severity="info" sx={{ borderRadius: "10px" }}>
          <Typography sx={{ fontFamily: PAGE_FONT, fontSize: 13 }}>
            {(summary.succeeded ?? 0).toLocaleString()} analyzed successfully
            {"; "}
            {(summary.failed ?? 0).toLocaleString()} could not be analyzed
            {Object.keys(failureCounts).length
              ? ` (${Object.entries(failureCounts)
                  .map(([code, count]) => `${count}× ${code}`)
                  .join(", ")})`
              : ""}
            . Failed products remain listed below.
          </Typography>
        </Alert>
      )}
      <LostSalesBulkTimeline
        bulkResult={bulkResult}
        productLabelById={productLabelById}
      />
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Lost Sales</TableCell>
              <TableCell align="right">Est. Lost Revenue</TableCell>
              <TableCell align="right">Stockout Days</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={`${row.scope?.product}-${row.scope?.sku}-${index}`}
              >
                <TableCell>
                  {resolveBulkProductLabel(row.scope, productLabelById)}
                </TableCell>
                <TableCell>
                  {row.scope?.analysis_level === "product"
                    ? "All child SKUs"
                    : row.scope?.sku || "—"}
                </TableCell>
                <TableCell align="right">
                  {row.status === "complete"
                    ? formatMetric(row.metrics?.estimated_lost_units)
                    : "—"}
                </TableCell>
                <TableCell
                  align="right"
                  title={
                    row.status === "complete"
                      ? describeRevenueEstimate(row.revenue_estimate).note || undefined
                      : undefined
                  }
                >
                  {row.status === "complete"
                    ? (() => {
                        const view = describeRevenueEstimate(row.revenue_estimate);
                        return view.state === "incomplete"
                          ? `${view.amountLabel}*`
                          : view.amountLabel;
                      })()
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {row.status === "complete"
                    ? formatMetric(row.metrics?.stockout_days)
                    : "—"}
                </TableCell>
                <TableCell>
                  {row.status === "complete" ? (
                    <Chip size="small" color="success" label="Succeeded" />
                  ) : (
                    <Chip
                      size="small"
                      color="error"
                      label={row.code || "Failed"}
                      title={row.message}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.status === "complete" && (
                    <Button size="small" onClick={() => onOpenDetail(row)}>
                      View
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default LostSalesAnalysisPage;
